import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import {
    TimelineItemData,
    TimelineItemRenderData,
    Timescale,
} from './Timeline';
import {
    avoidOverlappingObjects,
    findNextStart,
    findPrevEnd,
    roundTo,
} from './TimelineUtils';

import { assert } from '../../utils/assert';
import { clamp } from '../../utils/math';

const SCROLL_SENSITIVITY_Y = 1 / 500;
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;

export const useTimelineOperations = (
    timelineItems: TimelineItemRenderData[],
    timescale: Timescale,
) => {
    const onMove = (item: TimelineItemRenderData, value: TimelineItemData) => {
        const others = timelineItems.filter((other) => other.id !== item.id);

        value.start = timescale.roundTo(value.start);
        if (item.end) {
            const size = item.end?.getTime() - item.start.getTime();
            value.end = new Date(value.start.getTime() + size);
        }

        const avoidOverlapValue = avoidOverlappingObjects(value, others);
        if (value.lane === item.lane) {
            item.onChange?.(avoidOverlapValue);
        } else {
            const otherLaneDiff = Math.abs(
                avoidOverlapValue.start.getTime() - value.start.getTime(),
            );
            if (otherLaneDiff < timescale.stepSize * 2) {
                item.onChange?.(avoidOverlapValue);
            } else {
                // If switching lanes cause too much of a jump, stay in the same lane
                const sameLaneOverlapValue = avoidOverlappingObjects(
                    {
                        ...value,
                        lane: item.lane,
                    },
                    others,
                );
                item.onChange?.(sameLaneOverlapValue);
            }
        }
    };
    const onStartMove = (item: TimelineItemRenderData, newStart: Date) => {
        newStart = timescale.roundTo(newStart);
        const minStart = findPrevEnd(
            item.lane,
            item.start.getTime(),
            timelineItems,
        );

        const maxStart = (item.end?.getTime() ?? Infinity) - timescale.stepSize;

        item.onChange?.({
            start: new Date(clamp(newStart.getTime(), minStart, maxStart)),
        });
    };
    const onEndMove = (item: TimelineItemRenderData, newEnd: Date | null) => {
        if (newEnd) {
            newEnd = timescale.roundTo(newEnd);
        }

        const maxEnd = findNextStart(
            item.lane,
            item.end?.getTime() ?? Infinity,
            timelineItems,
        );

        const minEnd = item.start.getTime() + timescale.stepSize;

        const newEndMs = clamp(
            newEnd ? newEnd.getTime() : Infinity,
            minEnd,
            maxEnd,
        );
        item.onChange?.({
            end: new Date(newEndMs),
        });
    };
    const onEndExtend = (item: TimelineItemRenderData) => {
        const maxEnd = timelineItems
            .filter(
                (other) =>
                    other.id !== item.id &&
                    other.lane === item.lane &&
                    other.start.getTime() >= (item.end ?? item.start).getTime(),
            )
            .map((other) => {
                return other.start.getTime();
            })
            .reduce((a, b) => Math.min(a, b), Infinity);

        item.onChange?.({
            end: new Date(maxEnd),
        });
    };
    const onStartExtend = (item: TimelineItemRenderData) => {
        const itemsToTheLeft = timelineItems.filter(
            (other) =>
                other.id !== item.id &&
                other.lane === item.lane &&
                other.end &&
                other.end.getTime() <= item.start.getTime(),
        );

        if (itemsToTheLeft.length > 0) {
            const minStart = itemsToTheLeft
                .map((other) => {
                    assert(other.end);
                    return other.end.getTime();
                })
                .reduce((a, b) => Math.max(a, b), 0);
            item.onChange?.({
                start: new Date(minStart),
            });
        }
    };

    return {
        onMove,
        onStartMove,
        onEndMove,
        onEndExtend,
        onStartExtend,
    };
};

const TIME_SCALES: Array<Timescale> = [
    {
        label: '1 Month',
        stepSize: DAY_IN_MS * 31,
        roundTo: (date: Date) => {
            const copy = new Date(date);
            copy.setDate(1);
            copy.setHours(0, 0, 0, 0);
            const offset = copy.getTime();
            return new Date(roundTo(date.getTime(), DAY_IN_MS * 31, offset));
        },
    },
    {
        label: '1 Week',
        stepSize: DAY_IN_MS * 7,
        roundTo: (date: Date) => {
            const copy = new Date(date);
            copy.setDate(1);
            copy.setHours(0, 0, 0, 0);
            const offset = copy.getTime();
            return new Date(roundTo(date.getTime(), DAY_IN_MS * 7, offset));
        },
    },
    {
        label: '1 Day',
        stepSize: DAY_IN_MS,
        roundTo: (date: Date) => {
            const copy = new Date(date);
            copy.setHours(0, 0, 0, 0);
            const offset = copy.getTime();
            return new Date(roundTo(date.getTime(), DAY_IN_MS, offset));
        },
    },
    {
        label: '6 hours',
        stepSize: DAY_IN_MS / 4,
        roundTo: (date: Date) => {
            const copy = new Date(date);
            copy.setHours(0, 0, 0, 0);
            const offset = copy.getTime();
            return new Date(roundTo(date.getTime(), DAY_IN_MS / 4, offset));
        },
    },
    {
        label: '1 hour',
        stepSize: HOUR_IN_MS,
        roundTo: (date: Date) => {
            const copy = new Date(date);
            copy.setHours(0, 0, 0, 0);
            const offset = copy.getTime();
            return new Date(roundTo(date.getTime(), HOUR_IN_MS, offset));
        },
    },
];

export const useTimelineTransforms = ({
    lanes,
    laneHeight,
    pixelsPerMs,
    lowerBound,
    upperBound,
}: {
    lanes: number;
    laneHeight: number;
    pixelsPerMs: number;
    lowerBound: number;
    upperBound: number;
}) => {
    const positionToLane = useCallback(
        (position: number) => {
            const x = (position / (lanes * laneHeight)) * lanes;

            const minLane = 0;
            const maxLane = lanes - 1;
            return Math.min(maxLane, Math.max(minLane, Math.floor(x)));
        },
        [lanes, laneHeight],
    );
    const laneToPosition = useCallback(
        (lane: number) => (lane + 1) * laneHeight,
        [laneHeight],
    );
    const positionToTime = useCallback(
        (pixels: number) => pixels / pixelsPerMs + lowerBound,
        [pixelsPerMs, lowerBound],
    );
    const timeToPosition = useCallback(
        (ms: number) => (ms - lowerBound) * pixelsPerMs,
        [pixelsPerMs, lowerBound],
    );
    const isVisible = useCallback(
        (from: number, to?: number) => {
            if (to === undefined) {
                to = from;
            }
            return from < upperBound && lowerBound < to;
        },
        [lowerBound, upperBound],
    );

    return {
        positionToLane,
        laneToPosition,
        positionToTime,
        timeToPosition,
        isVisible,
    };
};

const MIN_SCALE = 1 / 15;
const MAX_SCALE = 20;
const INITIALS_PIXELS_PER_MS = 100 / DAY_IN_MS;
const PIXELS_PER_SLOT = 200;

export const useTimelineNavigation = ({
    lanes,
    laneHeight,
    timelineContainer,
}: {
    lanes: number;
    laneHeight: number;
    timelineContainer: HTMLDivElement | null;
}) => {
    const [scale, setScale] = useState(1);
    const pixelsPerMs = INITIALS_PIXELS_PER_MS * scale;
    const targetSlotSize = PIXELS_PER_SLOT / pixelsPerMs;
    const timescaleIndex = TIME_SCALES.findIndex(
        ({ stepSize }) => targetSlotSize > stepSize,
    );
    const timescale =
        timescaleIndex >= 0
            ? TIME_SCALES[timescaleIndex]
            : TIME_SCALES[TIME_SCALES.length - 1];
    const gridTimescale =
        timescaleIndex > 0 ? TIME_SCALES[timescaleIndex - 1] : undefined;

    const roundToResolution = useCallback(
        (date: Date) => {
            return timescale.roundTo(date);
        },
        [timescale],
    );

    const [lowerBound, setLowerBoundInternal] = useState(() =>
        roundToResolution(new Date(Date.now() - DAY_IN_MS * 14)).getTime(),
    );
    const [upperBound, setUpperBound] = useState(() =>
        roundToResolution(new Date(Date.now() + DAY_IN_MS * 14)).getTime(),
    );

    const { positionToTime, timeToPosition } = useTimelineTransforms({
        lanes,
        laneHeight,
        lowerBound,
        upperBound,
        pixelsPerMs,
    });

    const scrollRestoreRef = useRef<number | null>(null);
    // eslint-disable-next-line react-hooks/purity
    const scrollToRef = useRef<number | undefined>(Date.now());
    const currentMousePositionRef = useRef<{ time: number; x: number } | null>(
        null,
    );
    const zoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setLowerBound = useCallback(
        (lowerBound: number) => {
            if (zoomTimeoutRef.current === null) {
                const scrollLeft = timelineContainer?.scrollLeft;
                scrollRestoreRef.current = scrollLeft
                    ? positionToTime(scrollLeft)
                    : null;
            }

            setLowerBoundInternal(lowerBound);
        },
        [positionToTime, timelineContainer?.scrollLeft],
    );

    const scrollToInternal = useCallback(
        (time: number) => {
            timelineContainer?.scrollTo({
                left: timeToPosition(time) - timelineContainer?.clientWidth / 2,
                behavior: 'smooth',
            });
            scrollToRef.current = time;
            setTimeout(() => {
                scrollToRef.current = undefined;
            }, 500);
        },
        [timeToPosition, timelineContainer],
    );

    const scrollTo = useCallback(
        (time: number) => {
            if (time > upperBound) {
                setUpperBound(time + window.innerWidth / pixelsPerMs);
                scrollToRef.current = time;
            } else if (time < lowerBound) {
                setLowerBound(time - window.innerWidth / pixelsPerMs);
                scrollToRef.current = time;
            } else {
                scrollToInternal(time);
            }
        },
        [lowerBound, pixelsPerMs, scrollToInternal, setLowerBound, upperBound],
    );
    const setScaleExternal = (scale: number) => {
        const scrollLeft = timelineContainer?.scrollLeft;
        scrollRestoreRef.current = scrollLeft
            ? positionToTime(scrollLeft)
            : null;
        setScale(scale);
    };
    useLayoutEffect(() => {
        if (scrollRestoreRef.current !== null && timelineContainer) {
            // eslint-disable-next-line react-hooks/immutability
            timelineContainer.scrollLeft = timeToPosition(
                scrollRestoreRef.current,
            );
            scrollRestoreRef.current = null;
        }

        if (currentMousePositionRef.current && timelineContainer) {
            timelineContainer.scrollLeft = Math.round(
                timeToPosition(currentMousePositionRef.current.time) -
                    currentMousePositionRef.current.x,
            );
        }

        if (scrollToRef.current !== undefined) {
            scrollToInternal(scrollToRef.current);
        }
    }, [timeToPosition, timelineContainer, scrollToInternal]);

    useEffect(() => {
        const onWheel = (e: WheelEvent) => {
            if (
                e.shiftKey ||
                e.deltaX !== 0 ||
                scrollToRef.current !== undefined
            ) {
                return;
            }

            if (zoomTimeoutRef.current !== null) {
                clearTimeout(zoomTimeoutRef.current);
            }
            zoomTimeoutRef.current = setTimeout(() => {
                zoomTimeoutRef.current = null;
                currentMousePositionRef.current = null;
            }, 100);

            const currentTarget = e.currentTarget;
            assert(currentTarget instanceof HTMLElement);

            e.preventDefault();

            let pixelDeltaY: number;
            // let pixelDeltaX: number
            switch (e.deltaMode) {
                case WheelEvent.DOM_DELTA_PIXEL:
                    pixelDeltaY = e.deltaY;
                    // pixelDeltaX = e.deltaX
                    break;
                case WheelEvent.DOM_DELTA_LINE:
                    pixelDeltaY =
                        e.deltaY *
                        guessLineHeight(e.currentTarget as HTMLElement);
                    // pixelDeltaX =
                    // e.deltaX * getLineHeight(e.currentTarget as HTMLElement)
                    break;
                case WheelEvent.DOM_DELTA_PAGE:
                    pixelDeltaY = e.deltaY * window.innerHeight;
                    // pixelDeltaX = e.deltaX * window.innerWidth
                    break;
                default:
                    throw new Error(`Unknown DeltaMode: ${e.deltaMode}`);
            }

            const zoomDelta = pixelDeltaY * SCROLL_SENSITIVITY_Y;
            const newScale = scale - scale * zoomDelta;

            // offsetX is relative to target, which means it'd "jump around"
            // if the mouse hovers over a TimelineItem, instead we calculate
            // from clientX and subtract offset on the page
            const rect = currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - rect.left + window.scrollX;
            const mouseTime =
                currentMousePositionRef.current?.time ??
                positionToTime(mouseX + currentTarget.scrollLeft);
            currentMousePositionRef.current = { x: mouseX, time: mouseTime };

            setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale)));
        };

        timelineContainer?.addEventListener('wheel', onWheel);
        return () => {
            timelineContainer?.removeEventListener('wheel', onWheel);
        };
    }, [timelineContainer, scale, positionToTime]);

    useEffect(() => {
        const onScroll = (e: Event) => {
            if (scrollToRef.current !== undefined) {
                return;
            }

            const target = e.target;
            assert(target instanceof HTMLElement);

            const scrollLeft = target.scrollLeft;
            const scrollRight =
                timeToPosition(upperBound) -
                target.clientWidth -
                target.scrollLeft;

            // Expand
            const screenMs = target.clientWidth / pixelsPerMs;
            const paddingMs = screenMs;
            if (scrollLeft / pixelsPerMs < paddingMs) {
                setLowerBound(lowerBound - screenMs);
            }
            if (scrollRight / pixelsPerMs < paddingMs) {
                setUpperBound(upperBound + screenMs);
            }

            // Contract
            if (scrollLeft / pixelsPerMs > paddingMs * 2) {
                setLowerBound(lowerBound + screenMs);
            }

            if (scrollRight / pixelsPerMs > paddingMs * 2) {
                setUpperBound(upperBound - screenMs);
            }
        };

        timelineContainer?.addEventListener('scroll', onScroll);
        return () => {
            timelineContainer?.removeEventListener('scroll', onScroll);
        };
    }, [
        timelineContainer,
        pixelsPerMs,
        lowerBound,
        upperBound,
        timeToPosition,
        setLowerBound,
    ]);

    return {
        lowerBound,
        upperBound,
        timescale,
        gridTimescale,
        scrollTo,
        pixelsPerMs,
        scale,
        setScale: setScaleExternal,
        MIN_SCALE,
        MAX_SCALE,
    };
};

function guessLineHeight(element: HTMLElement): number {
    const computedStyle = window.getComputedStyle(element);
    const lineHeightString = computedStyle.lineHeight;

    if (lineHeightString === 'normal') {
        // 'normal' is tricky, it's browser-dependent.
        // A common approximation is 1.2 times the font-size.
        const fontSizeString = computedStyle.fontSize;
        return parseFloat(fontSizeString) * 1.2;
    } else {
        return parseFloat(lineHeightString);
    }
}
